import { PieChart, Pie, Cell } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CreditCard } from 'lucide-react';

interface PlanDistribution {
  plan: string;
  count: number;
  color: string;
}

interface PlanDistributionChartProps {
  data: PlanDistribution[];
  isLoading?: boolean;
}

export function PlanDistributionChart({ data, isLoading }: PlanDistributionChartProps) {
  const chartConfig = data.reduce((acc, item) => {
    acc[item.plan] = {
      label: item.plan,
      color: item.color,
    };
    return acc;
  }, {} as Record<string, { label: string; color: string }>);

  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Distribuição por Plano
        </CardTitle>
        <CardDescription>Tenants por tipo de assinatura</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center h-[200px]">
            <Skeleton className="h-[180px] w-[180px] rounded-full" />
          </div>
        ) : data.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
            Nenhum tenant cadastrado
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row items-center gap-4">
            <ChartContainer config={chartConfig} className="h-[200px] w-[200px]">
              <PieChart>
                <ChartTooltip content={<ChartTooltipContent nameKey="plan" />} />
                <Pie
                  data={data}
                  dataKey="count"
                  nameKey="plan"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>
            
            <div className="flex-1 space-y-2 w-full lg:w-auto">
              {data.map((item) => (
                <div key={item.plan} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div 
                      className="h-3 w-3 rounded-full shrink-0" 
                      style={{ backgroundColor: item.color }} 
                    />
                    <span className="text-sm font-medium">{item.plan}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm font-semibold">{item.count}</span>
                    <span className="text-xs text-muted-foreground">
                      ({total > 0 ? Math.round((item.count / total) * 100) : 0}%)
                    </span>
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t mt-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Total</span>
                  <span className="text-lg font-bold">{total}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
